import isEqual from 'lodash/isEqual';
import { useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  type CompositionV3Ent,
  type GetPayV3Query,
  PAY_V3_QUERY,
  type PayV3Req,
} from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import type { SelectionListKey } from '../../../../../shared/selection-lists/types';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from '../../../filters/utils/composition-v3/getCompositionV3Filters';
import { normalizeCompositionEffectiveFilters } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getSecondaryPrimaryDimension } from '../../../filters/utils/get-secondary-primary-dimension';
import { getV3Taxonomies } from '../../../filters/utils/get-v3-taxonomies';
import {
  appendRoleSelectionToFilters,
  isRoleTaxonomyLevelId,
} from '../../../filters/utils/role-skill-ent-kind';
import { ChartFallback } from '../../Chart/ChartFallback';
import { useChart } from '../../Chart/ChartProvider';
import { isCompensationChartType } from '../../PageConfigProvider/types/chart';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import {
  type SupportedSecondaryDimensions,
  type TopEntity,
  TopNDataProvider,
} from '../TopNDataProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
} from '../types';
import { convertCompensationTopNData } from './convert-compensation-top-n-data';

type CompensationTopNDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: PayMetricValues;
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const CompensationTopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  exchangeRate,
  children,
}: CompensationTopNDataProviderProps) => {
  return (
    <TopNDataProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
    >
      {({ data, status }) => (
        <ChartFallback status={status}>
          {data && (
            <CompensationMultiQuery
              primaryDimension={primaryDimension}
              secondaryDimension={secondaryDimension}
              topEntities={data}
              metric={metric}
              exchangeRate={exchangeRate}
            >
              {children}
            </CompensationMultiQuery>
          )}
          {!data && <NoData />}
        </ChartFallback>
      )}
    </TopNDataProvider>
  );
};

type PrimaryDimensionSelectionKeys = Extract<
  SelectionListKey,
  | 'rics_k10'
  | 'rics_k50'
  | 'rics_k400'
  | 'company'
  | 'region'
  | 'country'
  | 'msa'
  | 'role_k7'
  | 'role_k150'
  | 'role_k1500'
  | 'role_k15000'
>;
const CompensationMultiQuery = ({
  primaryDimension,
  secondaryDimension,
  metric,
  topEntities,
  exchangeRate,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: PayMetricValues;
  topEntities: TopEntity[];
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const { usesStateBreakdownOverMsa } = useFilterContext();

  const { item } = useChart();
  const { pageState } = usePageState();
  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const chartType = item?.plot?.chartType;

  const selections = primaryFilter?.selections;
  const primarySelections =
    selections?.map(
      (
        selection
      ): Partial<Record<PrimaryDimensionSelectionKeys, number[]>> => ({
        [selection.levelId]: [
          Number(selection?.id?.split('_')?.slice(-1)?.[0]),
        ],
      })
    ) ?? [];

  const filteredPrimarySelections = selectedEntity
    ? primarySelections?.filter((selection) =>
        selection?.[
          selectedEntity.levelId as PrimaryDimensionSelectionKeys
        ]?.includes(Number(selectedEntity.id))
      )
    : primarySelections;

  const payEntities: CompositionV3Ent[] = topEntities.map((ent) => ({
    EntID: ent.EntID,
    EntType: ent.EntType,
  }));

  const normalizedFilters =
    normalizeCompositionEffectiveFilters(effectiveFilters);
  const v3BaseFilters = getCompositionV3Filters({
    primaryDimension: getSecondaryPrimaryDimension(secondaryDimension),
    effectiveFilters: normalizedFilters,
  });
  const v3Corrections = getCompositionV3Corrections(normalizedFilters);
  const v3Taxonomies = getV3Taxonomies(normalizedFilters);

  const compensationFilters = filteredPrimarySelections.map(
    (primarySelection): PayV3Req => {
      // `primarySelection` is `{ [levelId]: [id] }` — role levelIds re-encode
      // as `role_level_N`; every other level keeps its key in v3 filters.
      const [levelId, ids] = Object.entries(primarySelection)[0] ?? ['', []];
      const numericId = ids?.[0];
      const filters =
        isRoleTaxonomyLevelId(levelId) && numericId !== undefined
          ? appendRoleSelectionToFilters(v3BaseFilters, levelId, numericId)
          : { ...v3BaseFilters, [levelId]: ids };
      return {
        entities: payEntities,
        filters,
        corrections: v3Corrections,
        ...v3Taxonomies,
        ...(usesStateBreakdownOverMsa && {
          options: { geo_breakdown_state_over_msa: true },
        }),
      };
    }
  );

  const gqlClient = useClient();

  const [fetching, setFetching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [compensationData, setCompensationData] = useState<
    { dim: string; data: GetPayV3Query['pay_v3'] | undefined }[]
  >([]);

  const lastFilterRef = useRef<PayV3Req[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    (async () => {
      if (isEqual(lastFilterRef.current, compensationFilters)) {
        return;
      }

      setFetching(true);
      setHasError(false);

      try {
        const responses = (
          await Promise.all(
            compensationFilters.map((filter) =>
              gqlClient.query(PAY_V3_QUERY, { req: filter })
            )
          )
        ).map((response) => response.data?.pay_v3);

        lastFilterRef.current = compensationFilters;

        setCompensationData(
          responses.map((data, i) => ({
            data,
            dim: selections?.[i]?.name ?? '',
          }))
        );
      } catch {
        setHasError(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [compensationFilters, gqlClient]);

  const status = (() => {
    if (fetching) return DataProviderQueryStatus.Fetching;
    if (hasError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  if (!chartType || !isCompensationChartType(chartType)) {
    return children({ data: undefined, status: DataProviderQueryStatus.Idle });
  }

  return children({
    data: convertCompensationTopNData({
      data: compensationData,
      metric,
      chartType,
      exchangeRate,
    }),
    status,
  });
};
